import { useEffect, useState, useRef } from 'react';
import { filter, map } from 'rxjs/operators';

import { Checkbox, CheckboxGroup, Form, FormControl, RadioButton, RadioButtonGroup } from '@client:common/form';
import { Button } from '@client:common/button';
import { Icon } from '@client:common/icon';
import { Ripple } from '@client:common/effects/ripple';
import { Markdown } from '@client:common/Markdown';
import { useElementGeometry } from '@client:common/hooks/useElementGeometry';

import { Option, Page } from '../../models/FlowChart';

import './UserInputCollector.light.scss';
import './UserInputCollector.dark.scss';

interface Props {
  currentPage: Page;
  answerTracker: Map<Page, Option[]>;
  isMobile: boolean;
  onAnswerChange: (answer: Option[]) => void;
  onShowGlossary: (item: Option) => void;
  toggleRightDrawerOpen: () => void;
  rightDrawerOpen: boolean;
}

export function UserInputCollector(props: Props) {
  const { currentPage, answerTracker, onAnswerChange, onShowGlossary, isMobile } = props;
  const [answerFormControl, setAnswerFormControl] = useState(new FormControl<Option | Option[]>(null));
  const [activeOptionForGlossary, setActiveOptionForGlossary] = useState<Option>(null);

  if (process.env.NODE_ENV === 'production') {
    // This is intended as we only want to run this hook in production
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      const listener = (event: BeforeUnloadEvent) => {
        event.preventDefault();
        event.returnValue = ''; // Gecko + IE
        return ''; // Gecko + Webkit, Safari, Chrome etc
      };
      window.addEventListener('beforeunload', listener, false);
      return () => {
        window.removeEventListener('beforeunload', listener, false);
      };
    }, []);
  }

  useEffect(() => {
    setActiveOptionForGlossary(null);
    if (currentPage) {
      const previousValue = currentPage.multiple
        ? answerTracker.get(currentPage) || []
        : answerTracker.get(currentPage)?.[0] || null;
      const newAnswerFormControl = new FormControl(previousValue);
      const subscription = newAnswerFormControl
        .valueChanges()
        .pipe(
          filter(Boolean),
          map((answer) => (Array.isArray(answer) ? answer : [answer]))
        )
        .subscribe({
          next: onAnswerChange
        });
      setAnswerFormControl(newAnswerFormControl);
      return () => {
        subscription.unsubscribe();
      };
    }
    return () => {
      return;
    };
  }, [currentPage, answerTracker, onAnswerChange]);

  const showOptionDescriptionTrigger = (option: Option) => {
    if (!option.description || option.description?.length === 0) {
      return null;
    }
    return (
      <Button
        className={option === activeOptionForGlossary ? 'active-glossary' : ''}
        style='basic'
        color='accent'
        onClick={() => {
          if (isMobile) {
            onShowGlossary(option);
            setActiveOptionForGlossary(option);
          } else {
            const glossaryItem = option === activeOptionForGlossary ? null : option;
            onShowGlossary(glossaryItem);
            setActiveOptionForGlossary(glossaryItem);
            props.toggleRightDrawerOpen();
          }
        }}
      >
        <Ripple fixed />
        <Icon
          icon='info'
          style='outlined'
          className='info-glossary' />
      </Button>
    );
  };

  if (!currentPage) {
    return null;
  }

  return (
    <div className='user-input-collector'>
      {isMobile && !props.rightDrawerOpen &&
        (
          <div className="absolute">
            <Button
              color='primary'
              style='basic'
              onClick={() => props.toggleRightDrawerOpen()}
            >
              <Ripple fixed />
              <Icon
                icon={`${props.rightDrawerOpen ? "chevron_right" : isMobile ? "expand_more" : "chevron_left"}`}
                style='outlined' />
              {props.rightDrawerOpen ? "Hide Panel" : "View Matches"}
            </Button>
          </div>
        )}
      <header>
        <h2 className='user-input-collector__question'>
          <Markdown>{currentPage.question}</Markdown>
        </h2>
        <Description
          key={currentPage.UUID || currentPage.label}
          page={currentPage}
          isMobile={isMobile}
        />
      </header>
      <Form className='user-input-collector__form'>
        {currentPage.multiple ? (
          <CheckboxGroup
            label=''
            formControl={answerFormControl as FormControl<Option[]>}>
            {currentPage.options.map((option) => (
              <Checkbox
                key={option.label}
                label={option.label}
                value={option}>
                {showOptionDescriptionTrigger(option)}
              </Checkbox>
            ))}
          </CheckboxGroup>
        ) : (
          <RadioButtonGroup
            label=''
            formControl={answerFormControl}>
            {currentPage.options.map((option) => (
              <RadioButton
                key={option.label}
                label={<Markdown>{option.label}</Markdown>}
                value={option}>
                {showOptionDescriptionTrigger(option)}
              </RadioButton>
            ))}
          </RadioButtonGroup>
        )}
      </Form>
    </div>
  );
}

const Description = ({ page, isMobile }: { page: Page; isMobile: boolean }) => {
  const [localShouldRenderContent, setLocalShouldRenderContent] = useState(false);
  const { ref: localDescriptionRef, geometry: localGeometry } = useElementGeometry(false);
  const { ref: textRef, geometry: textGeometry } = useElementGeometry(false);
  const localContentRef = useRef<HTMLDivElement>(null);
  const descriptionContainerRef = useRef<HTMLDivElement>(null);
  const [localMaxHeight, setLocalMaxHeight] = useState<number | undefined>(undefined);
  const [localIsContentTruncated, setLocalIsContentTruncated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);
  const [initialLayoutComplete, setInitialLayoutComplete] = useState(false);

  useEffect(() => {
    if (localGeometry) {
      setLocalMaxHeight(localGeometry.height * 0.8);
      setLocalShouldRenderContent(true);
    }
  }, [localGeometry]);

  useEffect(() => {
    if (localContentRef.current && localMaxHeight) {
      const fullHeight = localContentRef.current.scrollHeight;
      setContentHeight(fullHeight);
      const isOverflowing = fullHeight > localMaxHeight;
      setLocalIsContentTruncated(isOverflowing);

      if (!initialLayoutComplete && !isMobile) {
        setInitialLayoutComplete(true);
        // setIsExpanded(true);
      }
    }
  }, [localMaxHeight, page?.description, localShouldRenderContent, initialLayoutComplete, isMobile]);

  const handleReadMore = () => {
    setIsExpanded(true);
    setTimeout(() => {
      if (descriptionContainerRef.current) {
        descriptionContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleViewLess = () => {
    setIsExpanded(false);
    if (descriptionContainerRef.current) {
      descriptionContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const setDescriptionRef = (element: HTMLDivElement | null) => {
    descriptionContainerRef.current = element;
    if (localDescriptionRef) {
      localDescriptionRef(element);
    }
  };

  const textOverflow = textGeometry?.height > localGeometry?.height;
  const hasDescription = page.description && page.description.length > 0;

  return (
    <div
      ref={setDescriptionRef}
      className='description-container'>
      {(
        <div className="content-wrapper">
          <div
            ref={localContentRef}
            className={`description-content ${textOverflow && localIsContentTruncated && !isExpanded ? 'truncated' : ''} ${!initialLayoutComplete ? 'no-transition' : ''}`}
            style={isExpanded ? { maxHeight: `${contentHeight}px` } : {}}
          >
            {page.description?.map((item: string) => (
              <p
                key={item.length}
                className='user-input-collector__question-description'
                ref={textRef}
              >
                <Markdown>{item}</Markdown>
              </p>
            ))}
          </div>
          {hasDescription && textOverflow && localIsContentTruncated && !isExpanded && (
            <div className="read-more-container">
              <button
                className="read-more-button"
                onClick={handleReadMore}>
                Read More...
              </button>
            </div>
          )}
          {hasDescription && textOverflow && isExpanded && (
            <div className="view-less-container">
              <button
                className="view-less-button"
                onClick={handleViewLess}>
                View Less
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
